import {
  CompleteHocuspocusProviderWebsocketConfiguration,
  HocuspocusProvider,
} from '@hocuspocus/provider';
import * as Y from 'yjs';

import { getCollaborationServiceUrl } from 'src/utils/collaboration/config';

/**
 * Callback invoked when the Y.Doc receives a remote update.
 */
export type OnRemoteUpdate = (doc: Y.Doc) => void;

/**
 * Callback invoked when the provider's connection status changes.
 */
export type OnStatusChange = (status: string) => void;

/**
 * Options for creating a collaboration connection.
 */
export interface CollaborationConnectOptions {
  /** The project ID (used as the document name) */
  projectId: string;
  /** Called when the Y.Doc is updated remotely */
  onRemoteUpdate: OnRemoteUpdate;
  /** Called when the connection status changes (optional) */
  onStatusChange?: OnStatusChange;
  /** Called when the provider disconnects (optional) */
  onDisconnect?: () => void;
  /** Override the WebSocket URL (for testing) */
  url?: string;
}

/**
 * Active connection state for a single project.
 */
interface ActiveConnection {
  provider: HocuspocusProvider;
  doc: Y.Doc;
  status: string;
  synced: boolean;
  // `true` until the server's `Authenticated` message confirms editor role.
  // Defaulting to read-only avoids flashing editable UI for viewers.
  readOnly: boolean;
  updateHandler: (update: Uint8Array, origin: unknown) => void;
}

/** Map of active connections keyed by project ID. */
const activeConnections = new Map<string, ActiveConnection>();

/** Listeners notified whenever a project's connection status changes. */
type StatusListener = (projectId: string, status: string) => void;
const statusListeners = new Set<StatusListener>();

/** Listeners notified whenever a project's read-only flag changes. */
type ReadOnlyListener = (projectId: string, readOnly: boolean) => void;
const readOnlyListeners = new Set<ReadOnlyListener>();

/** Listeners notified whenever a project's WebSocket authentication fails. */
type AuthFailureListener = (projectId: string, reason: string) => void;
const authFailureListeners = new Set<AuthFailureListener>();

/**
 * Subscribe to connection status changes across all projects.
 *
 * @param listener - Called with (projectId, status) on every status change
 * @returns An unsubscribe function
 */
export const addStatusChangeEventListener = (
  listener: StatusListener
): (() => void) => {
  statusListeners.add(listener);
  return () => {
    statusListeners.delete(listener);
  };
};

/**
 * Subscribe to read-only role changes across all projects. Fires once per
 * project when the server's `Authenticated` message arrives with the user's
 * effective role.
 *
 * @param listener - Called with (projectId, readOnly) on every change
 * @returns An unsubscribe function
 */
export const addReadOnlyChangeEventListener = (
  listener: ReadOnlyListener
): (() => void) => {
  readOnlyListeners.add(listener);
  return () => {
    readOnlyListeners.delete(listener);
  };
};

/**
 * Subscribe to WebSocket auth failures (401/403 from SCS). Fires when the
 * user is not logged in or lacks access to the project.
 *
 * @param listener - Called with (projectId, reason)
 * @returns An unsubscribe function
 */
export const addAuthFailureEventListener = (
  listener: AuthFailureListener
): (() => void) => {
  authFailureListeners.add(listener);
  return () => {
    authFailureListeners.delete(listener);
  };
};

const notifyStatusListeners = (projectId: string, status: string) => {
  statusListeners.forEach((listener) => listener(projectId, status));
};

const notifyReadOnlyListeners = (projectId: string, readOnly: boolean) => {
  readOnlyListeners.forEach((listener) => listener(projectId, readOnly));
};

const notifyAuthFailureListeners = (projectId: string, reason: string) => {
  authFailureListeners.forEach((listener) => listener(projectId, reason));
};

/**
 * Connect to the Collaboration Service for a given project.
 *
 * Creates a new Y.Doc, establishes a HocuspocusProvider WebSocket connection,
 * and wires up remote update observation. Authentication uses the browser's
 * Django `sessionid` cookie, which nginx forwards on the WebSocket upgrade
 * — there is no client-side token.
 *
 * @param options - Connection options
 * @returns The Y.Doc for the connection (for writing local changes)
 */
export const connect = (options: CollaborationConnectOptions): Y.Doc => {
  const {
    projectId,
    onRemoteUpdate,
    onStatusChange,
    onDisconnect,
    url = getCollaborationServiceUrl(),
  } = options;

  // If already connected, disconnect first
  if (activeConnections.has(projectId)) {
    disconnect(projectId);
  }

  const doc = new Y.Doc();

  // Websocket reconnection options — forwarded to HocuspocusProviderWebsocket
  // when no explicit websocketProvider is given
  const reconnectOptions: Partial<CompleteHocuspocusProviderWebsocketConfiguration> =
    {
      maxAttempts: 10,
      delay: 1000,
      factor: 2,
      timeout: 5000,
    };

  const provider = new HocuspocusProvider({
    ...reconnectOptions,
    url,
    // Keep the trailing slash on `/collaboration/` — nginx 301-redirects the
    // non-slash form, and WebSocket upgrades can't follow HTTP redirects.
    preserveTrailingSlash: true,
    name: projectId,
    document: doc,
    onSynced: ({ state }) => {
      if (state) {
        const conn = activeConnections.get(projectId);
        if (conn) {
          conn.synced = true;
        }
      }
    },
    onStatus: ({ status }) => {
      const conn = activeConnections.get(projectId);
      if (conn) {
        conn.status = status;
      }
      notifyStatusListeners(projectId, status);
      if (onStatusChange) {
        onStatusChange(status);
      }
    },
    onAuthenticated: ({ scope }) => {
      // Hocuspocus maps the server's `readOnly` flag on connectionConfig to
      // this scope. SCS sets `readOnly: true` for the viewer role.
      const readOnly = scope === 'readonly';
      const conn = activeConnections.get(projectId);
      if (conn && conn.readOnly !== readOnly) {
        conn.readOnly = readOnly;
      }
      notifyReadOnlyListeners(projectId, readOnly);
    },
    onAuthenticationFailed: ({ reason }) => {
      notifyAuthFailureListeners(projectId, reason);
      // Stop the reconnect loop — the user doesn't have access, retrying
      // won't help. The legacy save path takes over once we disconnect.
      disconnect(projectId);
    },
    onDisconnect: onDisconnect ?? (() => null),
  });

  // Listen for remote updates (updates from other peers via the server).
  // Updates that arrive during the initial sync are ignored — the client
  // already loaded the project via the legacy Cloud API path, so applying
  // the server's (potentially stale) initial state would overwrite local
  // changes the user may have made before the sync completed.
  const updateHandler = (_update: Uint8Array, origin: unknown) => {
    const conn = activeConnections.get(projectId);
    const isRemote = origin === provider;

    if (!isRemote) {
      return;
    }

    if (!conn?.synced) {
      return;
    }

    onRemoteUpdate(doc);
  };
  doc.on('update', updateHandler);

  activeConnections.set(projectId, {
    provider,
    doc,
    status: 'connecting',
    synced: false,
    readOnly: true,
    updateHandler,
  });

  return doc;
};

/**
 * Disconnect from the Collaboration Service for a given project.
 *
 * Destroys the HocuspocusProvider and cleans up the Y.Doc.
 *
 * @param projectId - The project to disconnect from
 */
export const disconnect = (projectId: string): void => {
  const connection = activeConnections.get(projectId);
  if (!connection) {
    return;
  }

  connection.doc.off('update', connection.updateHandler);
  connection.provider.destroy();
  connection.doc.destroy();
  activeConnections.delete(projectId);
  notifyStatusListeners(projectId, 'disconnected');
};

/**
 * Disconnect all active collaboration connections.
 * Called on window beforeunload or when the user logs out.
 */
export const disconnectAll = (): void => {
  const projectIds = Array.from(activeConnections.keys());
  projectIds.forEach((projectId) => disconnect(projectId));
};

/**
 * Get the Y.Doc for an active collaboration connection.
 *
 * @param projectId - The project to get the doc for
 * @returns The Y.Doc, or undefined if not connected
 */
export const getDoc = (projectId: string): Y.Doc | undefined => {
  return activeConnections.get(projectId)?.doc;
};

/**
 * Check whether a collaboration connection is active for a given project.
 * Returns true only when the WebSocket is fully connected, not during
 * the connecting or reconnecting phase.
 *
 * @param projectId - The project to check
 * @returns true if connected
 */
export const isConnected = (projectId: string): boolean => {
  const connection = activeConnections.get(projectId);
  return connection?.status === 'connected';
};

/**
 * Get the current connection status for a given project.
 *
 * @param projectId - The project to check
 * @returns The status string ('connecting', 'connected', 'disconnected'),
 *          or undefined if no connection exists
 */
export const getStatus = (projectId: string): string | undefined => {
  return activeConnections.get(projectId)?.status;
};

/**
 * Get the current read-only flag for a project's collaboration connection.
 *
 * @param projectId - The project to check
 * @returns true if the user has viewer role, false for editor, or undefined
 *          if there is no connection for the project
 */
export const isReadOnly = (projectId: string): boolean | undefined => {
  return activeConnections.get(projectId)?.readOnly;
};
